import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Button, useTheme } from '@material-ui/core'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumPrecioUnitario } from '../../../../api'
import {
  Concept,
  ConceptLayoutConfigurationModel,
  NewConceptPrice
} from '../../../../api/Argentia/endpoints/concepts/model/concepts'
import { EnumPriceCalculationType, EnumRedondeo } from '../../../../api/Argentia/enums'
import { ProductModel } from '../../../../api/Argentia/model/contracts'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import { EnumTipoRelacionProductoConcepto } from '../../../../api/oData/enums/enumTipoRelacionProductoConcepto'
import { PriceExpressionValidator } from '../../../../components'
import Page, { PageLayout } from '../../../../components/page/page'
import PageCenterColumn from '../../../../components/page/pageCenterColumn'
import PageHeader from '../../../../components/page/pageHeader'
import { enumToSelectOptionArray, enumToSelectOptionArrayDefaultValues } from '../../../../helpers'
import { getCustomerDomain } from '../../../../redux'
import { ConceptModificationType } from '../../../products/enums/conceptModificationType.enum'
import { createProductConceptPrice, fetchProductById } from '../../../products/products.service'
import { buildDiscriminantFormInputs, hasAnyDiscriminantField } from '../../concepts.helpers'
import {
  mapConceptModifiedFormToRequest,
  mapFormDataToProductConceptPrice,
  mapProductModelToConceptSelectOptions
} from '../../concepts.mapper'
import { ConceptModifiedFormModel } from '../../concepts.models'
import {
  createConceptModified,
  getBaseConceptByID,
  getConfigurationLayout
} from '../../concepts.service'
import { ConceptPriceCard } from '../../edit/components/conceptPriceCard'

interface ConceptModifiedAddPageProps {
  location?: {
    state: {
      productId?: number
      contractCode: string
      contractProductCode: string
      contractId: number
      contractProductId: number
      commodity: EnumCommodity
      conceptModificationType?: ConceptModificationType // Tipo de personalización: producto o contrato
      productContractStartDate?: string
      productContractEndDate?: string
    }
  }
}

const cleanPriceFields = (formState: ConceptModifiedFormModel): ConceptModifiedFormModel => {
  const {
    value,
    period1Value,
    period2Value,
    period3Value,
    period4Value,
    period5Value,
    period6Value,
    rounding,
    expression,
    priceType,
    unitPrice,
    ...rest
  } = formState
  return rest
}

interface ConceptModifiedFormValidation {
  conceptRelationType: boolean
  masterConceptId: boolean
  startDate: boolean
  priceType: boolean
  unitPrice: boolean
  rounding: boolean
  value: boolean
  expression: boolean
  allComplete: boolean
}

const initialFormValidation: ConceptModifiedFormValidation = {
  conceptRelationType: false,
  masterConceptId: false,
  startDate: false,
  priceType: true,
  unitPrice: false,
  rounding: false,
  value: false,
  expression: true,
  allComplete: false
}

export const ConceptModifiedAddPage: React.FC<ConceptModifiedAddPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'conceptsModified', 'conceptsEdit'])
  const history = useHistory()
  const theme = useTheme()
  const { info, error } = useNotify()

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [formData, setFormData] = useState<ConceptModifiedFormModel>({} as ConceptModifiedFormModel)
  const [formValidations, setFormValidations] = useState<ConceptModifiedFormValidation>({
    ...initialFormValidation
  })

  const [showPeriodValues, setShowPeriodValues] = useState<boolean>(false)
  const [invalidDates, setInvalidDates] = useState<boolean>(false)
  const [productDetail, setProductDetail] = useState<ProductModel | null>(null)
  const [conceptOptions, setConceptOptions] = useState<SelectOption[]>([])
  const [isFetchingConceptOptions, setIsFetchingConceptOptions] = useState<boolean>(false)
  const [selectedConceptRelationType, setSelectedConceptRelationType] = useState<
    EnumTipoRelacionProductoConcepto | undefined
  >(undefined)

  // PriceExpressionValidator states
  const [priceExpression, setPriceExpression] = useState<string | undefined>(undefined)
  const [expressionIsValid, setExpressionIsValid] = useState<boolean>(false)
  const [priceCalculationType, setPriceCalculationType] = useState<
    EnumPriceCalculationType | undefined
  >(EnumPriceCalculationType.Simple)
  const [isExpressionLoading, setIsExpressionLoading] = useState<boolean>(false)

  // Base concept prices for reference (when Formula is selected)
  const [baseConceptPrices, setBaseConceptPrices] = useState<NewConceptPrice[] | null>(null)
  const [isFetchingBaseConcept, setIsFetchingBaseConcept] = useState<boolean>(false)
  const [baseConceptConfiguration, setBaseConceptConfiguration] =
    useState<ConceptLayoutConfigurationModel | null>(null)

  const productId = props.location?.state?.productId
  const contractCode = props.location?.state?.contractCode
  const contractProductCode = props.location?.state?.contractProductCode
  const contractId = props.location?.state?.contractId
  const contractProductId = props.location?.state?.contractProductId
  const commodity = props.location?.state?.commodity
  const conceptModificationType = props.location?.state?.conceptModificationType
  const productContractStartDate = props.location?.state?.productContractStartDate
  const productContractEndDate = props.location?.state?.productContractEndDate

  const [showFormula, setShowFormula] = useState<boolean>(
    conceptModificationType === ConceptModificationType.PRODUCT
  )
  const [showPriceEdit, setShowPriceEdit] = useState<boolean>(true)

  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleSave = async () => {
    // Build final form data - mapper will handle which fields to send based on priceType
    const finalFormData = {
      ...formData,
      priceCalculationType: showFormula ? priceCalculationType : formData.priceCalculationType,
      expression: showFormula ? priceExpression : formData.expression
    }

    if (conceptModificationType === ConceptModificationType.PRODUCT) {
      const request = mapFormDataToProductConceptPrice(finalFormData, productId!, commodity!)

      const response = await createProductConceptPrice(request)
      if (response.success) {
        info(t('conceptsModified:forms.add.notifications.saveSuccess'))
        history.goBack()
        return
      } else {
        error(t('conceptsModified:forms.add.notifications.saveError'))
      }
    } else {
      const request = mapConceptModifiedFormToRequest(
        finalFormData,
        contractCode!,
        contractProductCode!,
        conceptOptions
      )

      const response = await createConceptModified(request)
      if (response.success) {
        info(t('conceptsModified:forms.add.notifications.saveSuccess'))
        history.goBack()
        return
      } else {
        error(t('conceptsModified:forms.add.notifications.saveError'))
      }
    }
  }

  // ----------------------------- Form Handlers ------------------------------------//

  const handleFormValidation = useCallback(
    (key: keyof ConceptModifiedFormValidation, value: boolean): void => {
      setFormValidations(prevState => {
        const formIsCompleted = Object.keys(initialFormValidation)
          .filter(k => k !== 'allComplete')
          .every(k => (k === key ? value : prevState[k as keyof ConceptModifiedFormValidation]))

        return {
          ...prevState,
          [key]: value,
          allComplete: formIsCompleted
        }
      })
    },
    []
  )

  const handlePeriodValueChange = useCallback(
    (fieldName: string, value: any, formState: any): void => {
      const periodFields = [
        'period1Value',
        'period2Value',
        'period3Value',
        'period4Value',
        'period5Value',
        'period6Value'
      ]
      const anyPeriodHasValue = periodFields.some(field => {
        if (field === fieldName) {
          return !!value && value !== '' && !isNaN(Number(value))
        }
        return !!formState[field] && formState[field] !== '' && !isNaN(Number(formState[field]))
      })

      handleFormValidation('value', anyPeriodHasValue)
    },
    [handleFormValidation]
  )

  // #endregion Handlers Ends

  // #region Functions Starts
  const validateDates = useCallback((startDate: string, endDate: string): void => {
    if (startDate && endDate) {
      const isInvalid = moment(startDate).isAfter(moment(endDate))
      setInvalidDates(isInvalid)
    } else {
      setInvalidDates(false)
    }
  }, [])
  // #endregion Functions Ends

  // #region Form Definition Starts
  const options: FormOptions = useMemo(
    () => ({
      elevation: 0,
      columns: 4,
      analytics: {
        customer: getCustomerDomain(),
        view: t('conceptsModified:forms.add.analytics')
      }
    }),
    [t]
  )

  const sections: Section[] = useMemo(
    () => [
      {
        title: t('conceptsModified:forms.add.title'),
        inputs: [
          {
            name: 'conceptRelationType',
            label: t('conceptsModified:forms.add.inputs.conceptRelationType'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(
              EnumTipoRelacionProductoConcepto,
              'enums:EnumTipoRelacionProductoConcepto'
            ),
            required: true,
            onChange: (value, formState) => {
              const isComplete = !!value && value !== ''
              handleFormValidation('conceptRelationType', isComplete)
              setSelectedConceptRelationType(value as EnumTipoRelacionProductoConcepto)

              // Update formData to persist the value across re-renders
              setFormData({
                ...(formState as ConceptModifiedFormModel),
                masterConceptId: undefined as any // Clear concept selection when type changes
              })

              // Clear master concept validation
              handleFormValidation('masterConceptId', false)
            }
          },
          {
            name: 'masterConceptId',
            label: t('conceptsModified:forms.add.inputs.masterConceptId'),
            type: DataType.SELECT,
            selectOptions: conceptOptions as SelectOption[],
            isLoading: isFetchingConceptOptions,
            required: true,
            onChange: (value, formState) => {
              const isComplete = !!value && value !== ''
              handleFormValidation('masterConceptId', isComplete)
            }
          },

          {
            name: 'productContractStartDate',
            label: t('conceptsModified:forms.edit.inputs.productContractStartDate'),
            type: DataType.DATE,
            readOnly: true,
            defaultValue: productContractStartDate,
            display: conceptModificationType !== ConceptModificationType.PRODUCT
          },
          {
            name: 'productContractEndDate',
            label: t('conceptsModified:forms.edit.inputs.productContractEndDate'),
            type: DataType.DATE,
            readOnly: true,
            defaultValue: productContractEndDate,
            display: conceptModificationType !== ConceptModificationType.PRODUCT
          },
          {
            name: 'startDate',
            label: t('conceptsModified:forms.add.inputs.startDate'),
            type: DataType.DATE,
            required: true,
            errorState: invalidDates,
            errorText: t('conceptsModified:forms.add.errorText.invalidStartDate'),
            onChange: (value, formState) => {
              const isComplete = !!value
              const formData = formState as ConceptModifiedFormModel
              handleFormValidation('startDate', isComplete)
              validateDates(
                value ? value.toString() : '',
                formData?.endDate ? formData.endDate.toString() : ''
              )
            }
          },
          {
            name: 'endDate',
            label: t('conceptsModified:forms.add.inputs.endDate'),
            type: DataType.DATE,
            clearable: true,
            errorState: invalidDates,
            errorText: t('conceptsModified:forms.add.errorText.invalidEndDate'),
            onChange: (value, formState) => {
              const formData = formState as ConceptModifiedFormModel
              validateDates(
                formData?.startDate ? formData.startDate.toString() : '',
                value ? value.toString() : ''
              )
            }
          },
          {
            name: 'customDescription',
            label: t('conceptsModified:forms.add.inputs.customDescription'),
            type: DataType.TEXT,
            display: conceptModificationType !== ConceptModificationType.PRODUCT,
            onChange: (value, formState) => {}
          },
          {
            name: 'priceEdit',
            label: t('conceptsModified:forms.add.inputs.priceEdit'),
            type: DataType.SWITCH,
            defaultValue: true,
            display: conceptModificationType !== ConceptModificationType.PRODUCT,
            onChange: (value, formState) => {
              const isPriceEditEnabled = !!value
              setShowPriceEdit(isPriceEditEnabled)

              const currentState = formState as ConceptModifiedFormModel

              const priceFieldsNotRequired = !isPriceEditEnabled
              handleFormValidation('priceType', priceFieldsNotRequired)
              handleFormValidation('unitPrice', priceFieldsNotRequired)
              handleFormValidation('rounding', priceFieldsNotRequired)
              handleFormValidation('value', priceFieldsNotRequired)
              handleFormValidation('expression', priceFieldsNotRequired)

              if (!isPriceEditEnabled) {
                setFormData(cleanPriceFields(currentState))
              }
            }
          },
          {
            name: 'priceType',
            label: t('conceptsModified:forms.add.inputs.priceType'),
            type: DataType.SELECT,
            display: showPriceEdit || conceptModificationType === ConceptModificationType.PRODUCT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumPriceType,
              'enums:EnumPriceTypes',
              [EnumPriceType.Parametro, EnumPriceType.ParametroPeriodo, EnumPriceType.Formula]
            ),
            defaultValue:
              conceptModificationType === ConceptModificationType.PRODUCT
                ? EnumPriceType.Formula
                : undefined,
            required: showPriceEdit || conceptModificationType === ConceptModificationType.PRODUCT,
            readOnly: conceptModificationType === ConceptModificationType.PRODUCT,
            onChange: (value, formState) => {
              const isComplete = !!value && value !== ''
              handleFormValidation('priceType', isComplete)
              const isFormulaType = value === EnumPriceType.Formula
              const isPeriodType = value === EnumPriceType.ParametroPeriodo
              setShowPeriodValues(isPeriodType)
              setShowFormula(isFormulaType)

              // Clean formData based on the selected price type using destructuring
              const currentState = formState as ConceptModifiedFormModel
              let cleanedFormState: ConceptModifiedFormModel

              if (isFormulaType) {
                // Formula: clear value fields and rounding, keep expression
                const {
                  value: _v,
                  period1Value: _p1,
                  period2Value: _p2,
                  period3Value: _p3,
                  period4Value: _p4,
                  period5Value: _p5,
                  period6Value: _p6,
                  rounding: _r,
                  ...rest
                } = currentState
                cleanedFormState = rest
                handleFormValidation('value', true) // Value not required for formula
                handleFormValidation('expression', false) // Expression pending validation
                handleFormValidation('rounding', true) // Rounding not required for formula
              } else if (isPeriodType) {
                // Period: clear single value and expression
                const {
                  value: _v,
                  expression: _e,
                  priceCalculationType: _pc,
                  ...rest
                } = currentState
                cleanedFormState = rest
                setPriceExpression(undefined)
                setExpressionIsValid(false)
                handleFormValidation('expression', true) // Expression not required
                handleFormValidation('value', false) // Need at least one period value
                handleFormValidation('rounding', !currentState.rounding) // Rounding required, check if already set
              } else {
                // Parametro: clear period values and expression
                const {
                  period1Value: _p1,
                  period2Value: _p2,
                  period3Value: _p3,
                  period4Value: _p4,
                  period5Value: _p5,
                  period6Value: _p6,
                  expression: _e,
                  priceCalculationType: _pc,
                  ...rest
                } = currentState
                cleanedFormState = rest
                setPriceExpression(undefined)
                setExpressionIsValid(false)
                handleFormValidation('expression', true) // Expression not required
                handleFormValidation('value', false) // Value required for parametro
                handleFormValidation('rounding', !currentState.rounding) // Rounding required, check if already set
              }

              setFormData(cleanedFormState)
            }
          },
          {
            name: 'unitPrice',
            label: t('conceptsModified:forms.add.inputs.unitPrice'),
            type: DataType.SELECT,
            display: showPriceEdit || conceptModificationType === ConceptModificationType.PRODUCT,
            selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit'),
            required: showPriceEdit || conceptModificationType === ConceptModificationType.PRODUCT,
            onChange: (value, formState) => {
              const isComplete = !!value && value !== ''
              handleFormValidation('unitPrice', isComplete)
            }
          },
          {
            name: 'rounding',
            label: t('conceptsModified:forms.add.inputs.rounding'),
            type: DataType.SELECT,
            display: !showFormula && showPriceEdit,
            selectOptions: enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo'),
            required: !showFormula && showPriceEdit,
            onChange: (value, formState) => {
              const isComplete = !!value && value !== ''
              handleFormValidation('rounding', isComplete)

              // Update formData to trigger re-render with new decimal scale
              const currentState = formState as ConceptModifiedFormModel
              setFormData({ ...currentState, rounding: value as EnumRedondeo })
            }
          }
        ]
      },
      // Discriminantes de precio (región impuestos, peaje, etc.) según layout del concepto.
      // Se muestran solos cuando es Fórmula (los valores van en la sección de Fórmula más abajo);
      // cuando no es Fórmula, se fusionan con la sección de Valores para no duplicar el título.
      ...(showPriceEdit &&
      baseConceptConfiguration &&
      conceptModificationType === ConceptModificationType.PRODUCT &&
      showFormula &&
      hasAnyDiscriminantField(baseConceptConfiguration)
        ? [
            {
              title: t('conceptsModified:forms.add.valuesTitle'),
              inputs: buildDiscriminantFormInputs(
                baseConceptConfiguration,
                t,
                (field, value, formState) => {
                  setFormData({ ...(formState as ConceptModifiedFormModel), [field]: value })
                }
              )
            }
          ]
        : []),
      // Only show values section when not using Formula type
      ...(!showFormula && showPriceEdit
        ? [
            {
              title: t('conceptsModified:forms.add.valuesTitle'),
              inputs: [
                // Discriminantes de precio (región impuestos, peaje, etc.) según layout del concepto
                ...(baseConceptConfiguration &&
                conceptModificationType === ConceptModificationType.PRODUCT
                  ? buildDiscriminantFormInputs(
                      baseConceptConfiguration,
                      t,
                      (field, value, formState) => {
                        setFormData({ ...(formState as ConceptModifiedFormModel), [field]: value })
                      }
                    )
                  : []),
                {
                  name: 'value',
                  label: t('conceptsModified:forms.add.inputs.value'),
                  type: DataType.NUMBER,
                  display: !showPeriodValues,
                  required: showPriceEdit,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    const isComplete = !!value && value !== '' && !isNaN(Number(value))
                    handleFormValidation('value', isComplete)
                  }
                },
                {
                  name: 'period1Value',
                  label: t('conceptsModified:forms.add.inputs.period1Value'),
                  type: DataType.NUMBER,
                  display: showPeriodValues,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    handlePeriodValueChange('period1Value', value, formState)
                  }
                },
                {
                  name: 'period2Value',
                  label: t('conceptsModified:forms.add.inputs.period2Value'),
                  type: DataType.NUMBER,
                  display: showPeriodValues,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    handlePeriodValueChange('period2Value', value, formState)
                  }
                },
                {
                  name: 'period3Value',
                  label: t('conceptsModified:forms.add.inputs.period3Value'),
                  type: DataType.NUMBER,
                  display: showPeriodValues,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    handlePeriodValueChange('period3Value', value, formState)
                  }
                },
                {
                  name: 'period4Value',
                  label: t('conceptsModified:forms.add.inputs.period4Value'),
                  type: DataType.NUMBER,
                  display: showPeriodValues,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    handlePeriodValueChange('period4Value', value, formState)
                  }
                },
                {
                  name: 'period5Value',
                  label: t('conceptsModified:forms.add.inputs.period5Value'),
                  type: DataType.NUMBER,
                  display: showPeriodValues,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    handlePeriodValueChange('period5Value', value, formState)
                  }
                },
                {
                  name: 'period6Value',
                  label: t('conceptsModified:forms.add.inputs.period6Value'),
                  type: DataType.NUMBER,
                  display: showPeriodValues,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    handlePeriodValueChange('period6Value', value, formState)
                  }
                }
              ]
            }
          ]
        : []),
      ...(showFormula &&
      (showPriceEdit || conceptModificationType === ConceptModificationType.PRODUCT)
        ? [
            {
              title: t('conceptsModified:forms.add.expressionTitle'),
              inputs: []
            }
          ]
        : [])
    ],
    [
      t,
      formData,
      showFormula,
      showPeriodValues,
      invalidDates,
      conceptOptions,
      isFetchingConceptOptions,
      handleFormValidation,
      validateDates,
      handlePeriodValueChange,
      conceptModificationType,
      showPriceEdit,
      baseConceptConfiguration
    ]
  )
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    if (conceptModificationType === ConceptModificationType.PRODUCT) {
      handleFormValidation('value', true) // Value not required for formula
      handleFormValidation('rounding', true) // Rounding not required for formula
    }
  }, [conceptModificationType, handleFormValidation])

  useEffect(() => {
    // Update expression validation when expressionIsValid changes
    if (showFormula) {
      handleFormValidation('expression', expressionIsValid)
    }
  }, [expressionIsValid, showFormula, handleFormValidation])

  useEffect(() => {
    // Fetch base concept prices when masterConceptId changes and Formula is selected
    const fetchBaseConcept = async (conceptId: number) => {
      setIsFetchingBaseConcept(true)
      try {
        const response = await getBaseConceptByID(conceptId)
        if (response.success && response.data) {
          const concept = response.data as Concept
          setBaseConceptPrices((concept.conceptPrice as NewConceptPrice[]) ?? null)

          // Fetch the real configuration layout based on calculationType
          if (concept.calculationType) {
            const configResponse = await getConfigurationLayout(concept.calculationType)
            if (configResponse.success && configResponse.data) {
              setBaseConceptConfiguration(configResponse.data as ConceptLayoutConfigurationModel)
            }
          }
        }
      } catch (error) {
        console.error('Error fetching base concept:', error)
      } finally {
        setIsFetchingBaseConcept(false)
      }
    }

    if (formData?.masterConceptId) {
      // Se necesita la configuración del concepto tanto para Fórmula (tarjeta de referencia)
      // como para Parametro/ParametroPeriodo (campos discriminantes: región impuestos, etc.)
      fetchBaseConcept(formData.masterConceptId)
    } else {
      setBaseConceptPrices(null)
      setBaseConceptConfiguration(null)
    }
  }, [formData?.masterConceptId])

  useEffect(() => {
    const fetchConceptOptions = async (
      productID: number,
      relationType: EnumTipoRelacionProductoConcepto
    ) => {
      setIsFetchingConceptOptions(true)
      try {
        const response = await fetchProductById(productID)
        if (response.success && response.data) {
          const product = response.data as ProductModel
          setProductDetail(product)
          const options = mapProductModelToConceptSelectOptions(product, relationType)
          setConceptOptions(options || [])
        }
      } catch (error) {
        console.error('Error fetching concept options:', error)
        setConceptOptions([])
      } finally {
        setIsFetchingConceptOptions(false)
      }
    }

    // Only fetch if we have the required parameters
    if (!productId || selectedConceptRelationType === undefined) {
      return
    }

    // If we don't have product detail yet, fetch it
    if (!productDetail?.relatedConcepts) {
      fetchConceptOptions(productId, selectedConceptRelationType)
      return
    }

    // If we already have product detail, just filter the concepts
    const options = mapProductModelToConceptSelectOptions(
      productDetail,
      selectedConceptRelationType
    )
    setConceptOptions(options || [])
  }, [productId, selectedConceptRelationType])
  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <PageHeader title={t('conceptsModified:header.addTitle')}>
          <Button
            size="small"
            variant="text"
            color="primary"
            disabled={isFetching}
            onClick={handleBackButtonClick}
          >
            {t('conceptsModified:header.buttons.goBack')}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={isFetching || !formValidations.allComplete || invalidDates}
            onClick={handleSave}
          >
            {t('conceptsModified:header.buttons.save')}
          </Button>
        </PageHeader>
        {/* MAIN BODY COMPONENTS */}
        <Form sections={sections} data={formData} options={options} />

        {/* Price Expression Validator - Only for Formula type */}
        {showFormula &&
          commodity &&
          (showPriceEdit || conceptModificationType === ConceptModificationType.PRODUCT) && (
            <>
              <PriceExpressionValidator
                show={true}
                priceType={EnumPriceType.Formula}
                commodity={commodity}
                showPriceUnit={false}
                showRounding={false}
                showVersion={false}
                showDates={false}
                elevation={0}
                activePriceCalculationType={priceCalculationType}
                activeExpression={priceExpression}
                activeExpressionIsValid={expressionIsValid}
                activePriceUnit={formData?.unitPrice}
                activeRounding={formData?.rounding}
                showValidationHelper={true}
                onSelectPriceCalculationType={value => {
                  setPriceCalculationType(value)
                }}
                onSelectValidExpression={value => {
                  setPriceExpression(value)
                  // Update formData with expression
                  setFormData({ ...formData, expression: value })
                }}
                onSelectPriceUnit={value => {}} // Not used, hidden
                onSelectRounding={value => {}} // Not used, hidden
                onIsValid={value => {
                  setExpressionIsValid(value)
                }}
                onIsLoading={setIsExpressionLoading}
              />
            </>
          )}

        {/* Base Concept Price Reference - Read-only */}
        {showFormula &&
          formData?.masterConceptId &&
          baseConceptConfiguration &&
          (showPriceEdit || conceptModificationType === ConceptModificationType.PRODUCT) && (
            <ConceptPriceCard
              isFetching={isFetchingBaseConcept}
              conceptPrice={baseConceptPrices}
              conceptId={formData.masterConceptId}
              configuration={baseConceptConfiguration}
              readOnly={true}
              raised={false}
              commodity={commodity!}
            />
          )}
      </PageCenterColumn>
    </Page>
  )
}
